import { Description, Folder as FolderIcon } from '@mui/icons-material';
import ContactsIcon from '@mui/icons-material/Contacts';
import { useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TilgangstypeEnum } from '../../models/Enum/Tilgangstype';
import { AcosIcon } from '../common/controls/AcosIcon';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Credentials, useAuth } from './auth';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { AppLayout, NoMatch, NotImplemented } from '../../App';
import { useBase } from './utilHooks';


const FylkerListe = React.lazy(() => import(/* webpackChunkName: "Fylker" */ '../../features/fylkerListe/FylkerListe'));
const Grupper = React.lazy(() => import(/* webpackChunkName: "Grupper" */ '../../features/grupper/Grupper'));
const IdentitetListe = React.lazy(() => import(/* webpackChunkName: "Identiteter" */ '../../features/identitetListe/IdentitetListe'));

const KommuneList = React.lazy(() => import(/* webpackChunkName: "Kommuner" */ '../../features/kommuner/KommuneList'));
const Identitetskategorier = React.lazy(() => import(/* webpackChunkName: "Identitetskategorier" */ '../../features/identitetskategorier/Identitetskategorier'));
const SpraakListe = React.lazy(() => import(/* webpackChunkName: "Språk" */  '../../features/spraakListe/SpraakListe'));
const LandListe = React.lazy(() => import(/* webpackChunkName: "Land" */ '../../features/landListe/LandListe'));
const PostnummerListe = React.lazy(() => import(/* webpackChunkName: "Postnummer" */ '../../features/postnummer/PostnummerListe'));
const TilgangsgruppeListe = React.lazy(() => import(/* webpackChunkName: "Tilgangsgruppe" */ '../../features/tilgangsgruppe/TilgangsgruppeListe'));

const ArkivListe = React.lazy(() => import(/* webpackChunkName: "Arkiv" */ '../../features/arkiver/arkivListe'));
const ArkivPeriodeListe = React.lazy(() => import(/* webpackChunkName: "ArkivPeriode" */ '../../features/arkivPerioder/arkivPeriodeListe'));
const ArkivStatusListe = React.lazy(() => import(/* webpackChunkName: "ArkivStatus" */ '../../features/arkivStatus/arkivStatusListe'));
const LovverkListe = React.lazy(() => import(/* webpackChunkName: "Lovverk" */  '../../features/Lovverk/LovverkListe'));
const OrdningsPrinsippTypeListe = React.lazy(() => import(/* webpackChunkName: "OrdningsPrinsippType" */  '../../features/ordningsPrinsippType/OrdningsPrinsippTypeList'));
const OrdningsPrinsippListe = React.lazy(() => import(/* webpackChunkName: "OrdningsPrinsipp" */  '../../features/ordningsPrinsipp/OrdningsPrinsippList'));
const BortsettingsKoderListe = React.lazy(() => import(/* webpackChunkName: "BortsettingsKoder" */  '../../features/bortsettingsKoder/bortsettingsKodeListe'));
const ParagraferListe = React.lazy(() => import(/* webpackChunkName: "Paragrafer" */  '../../features/paragraf/ParagraferList'));
const TilgangsKodeListe = React.lazy(() => import(/* webpackChunkName: "TilgangsKode" */  '../../features/tilgangsKoder/TilgansKoderList'));
const KassasjonsKodeListe = React.lazy(() => import(/* webpackChunkName: "KassasjonsKoder" */  '../../features/kassasjonsKoder/kassasjonsKodeListe'));
const KassasjonOgBeingsVedtakList = React.lazy(() => import(/* webpackChunkName: "kassasjonOgBeingsVedtak" */  '../../features/kassasjonOgBevaringsVedtak/kassasjonOgBevaringsVedtakList'));
const AvskjermingKodeListe = React.lazy(() => import(/* webpackChunkName: "AvskjermingKoder" */  '../../features/avskjermingKoder/avskjermingKodeListe'));
const Journalenheter = React.lazy(() => import(/* webpackChunkName: "Journalenheter" */  '../../features/journalenheter/Journalenheter'));
const OppgaverList = React.lazy(() => import(/* webpackChunkName: "OppgaverList" */  '../../features/oppgaver/OppgaverList'));
const ArkivdelList = React.lazy(() => import(/* webpackChunkName: "ArkivdelList" */  '../../features/arkivdel/ArkivdelList'));
const OrdningsVerdierList = React.lazy(() => import(/* webpackChunkName: "ArkivdelList" */  '../../features/ordningsVerdi/OrdningsVerdierList'));


const EksterneLinker = React.lazy(() => import(/* webpackChunkName: "EksterneLinker" */  '../../features/eksternLink/EksternLinkList'));
const Klokketyper = React.lazy(() => import(/* webpackChunkName: "Klokketyper" */  '../../features/klokketyper/klokketypeListe'));
const Tager = React.lazy(() => import(/* webpackChunkName: "Tager" */  '../../features/tag/TagerList'));

const Dokumentkategories = React.lazy(() => import(/* webpackChunkName: "Dokumentkategories" */  '../../features/dokumentkategori/DokumentkategoriList'));
const Dokumentstatus = React.lazy(() => import(/* webpackChunkName: "DokumentstatusList" */  '../../features/dokumentstatus/DokumentstatusList'));
const DokumentTilknytning = React.lazy(() => import(/* webpackChunkName: "DokumentTilknytningList" */  '../../features/dokumentTilknytning/DokumentTilknytningList'));
const FormatVariant = React.lazy(() => import(/* webpackChunkName: "FormatVariantList" */  '../../features/formatVariant/FormatVariantList'));
const Forsendelsesmate = React.lazy(() => import(/* webpackChunkName: "ForsendelsesmaterList" */  '../../features/forsendelsesmate/ForsendelsesmaterList'));
const Tekstmaler = React.lazy(() => import(/* webpackChunkName: "TekstmalList" */  '../../features/tekstmal/TekstmalListe'));

const Avskrivningsmate = React.lazy(() => import(/* webpackChunkName: "AvskrivningsmateList" */  '../../features/avskrivningsmate/AvskrivningsmateList'));
const Behandlingstype = React.lazy(() => import(/* webpackChunkName: "BehandlingstypeList" */  '../../features/behandlingstyper/BehandlingstypeList'));
const Dokumenttyper = React.lazy(() => import(/* webpackChunkName: "Dokumenttyper" */  '../../features/dokumenttype/DokumenttypeList'));
const Journalkategorier = React.lazy(() => import(/* webpackChunkName: "Journalkategorier" */  '../../features/journalkategori/Journalkategorier'));
const Journalstatuser = React.lazy(() => import(/* webpackChunkName: "Journalstatuser" */  '../../features/journalStatus/JournalstatusList'));
const MalJournalposter = React.lazy(() => import(/* webpackChunkName: "Journalstatuser" */  '../../features/malJournalpost/MalJournalpostList'));
const Prioreteter = React.lazy(() => import(/* webpackChunkName: "Prioriteter" */  '../../features/prioritet/PrioretetList'));

//#region  Organisasjon
const AdmEnheter = React.lazy(() => import(/* webpackChunkName: "AdmEnheter" */  '../../features/admEnhet/AdmEnhetList'));
const Enhetstyper = React.lazy(() => import(/* webpackChunkName: "Enhetstyper" */  '../../features/enhetstyper/EnhetstyperList'));
const Brukere = React.lazy(() => import(/* webpackChunkName: "Brukere" */ '../../features/brukere/Brukere'));
const Organisering = React.lazy(() => import(/* webpackChunkName: "OrganiseringList" */ '../../features/organisering/OrganiseringList'));
//#endregion Organisasjon

const Tilgangsfunksjoner = React.lazy(() => import(/* webpackChunkName: "Tilgangsfunksjoner" */  '../../features/tilgangsfunksjon/Tilgangsfunksjon'));


const Tekstkataloger = React.lazy(() => import(/* webpackChunkName: "TekstkatalogListe" */  '../../features/tekstkatalog/tekstkatalogListe'));


//#region  Sak
const AvgjorelsesKode = React.lazy(() => import(/* webpackChunkName: "AvgjorelsesKodeList" */  '../../features/avgjorelsesKode/AvgjorelsesKodeList'));

const Sakstatus = React.lazy(() => import(/* webpackChunkName: "SakstatusListe" */  '../../features/sakstatus/SakstatusListe'));
const MalsakList = React.lazy(() => import(/* webpackChunkName: "MalsakList" */  '../../features/saksmaler/MalsakList'));

const SakstypeUniqueList = React.lazy(() => import(/* webpackChunkName: "SakstypeUniqueList" */  '../../features/sakstypeUnique/SakstypeUniqueList'));
const PartsforholdList = React.lazy(() => import(/* webpackChunkName: "PartsforholdList" */  '../../features/partsforhold/PartsforholdList'));
const ArkivSakList = React.lazy(() => import(/* webpackChunkName: "PartsforholdList"*/'../../features/arkivSak/ArkivSakList'));

//#endregion Sak


//#region System
const Databaser = React.lazy(() => import(/* webpackChunkName: "DatabaseListe" */  '../../features/databaser/DatabaseListe'));
const SearchClient = React.lazy(() => import(/* webpackChunkName: "SearchClient" */  '../../features/searchClient/SearchClient'));
const Logg = React.lazy(() => import(/* webpackChunkName: "LoggList" */  '../../features/logg/loggList'));
const FellesEpostbokser = React.lazy(() => import(/* webpackChunkName: "FellesEpostboksListe" */  '../../features/fellesEpostboks/FellesEpostboksListe'));
const Informasjonstyper = React.lazy(() => import(/* webpackChunkName: "InformasjonstypeListe" */  '../../features/informasjonstype/InformasjonstypeListe'));


const Funksjon = React.lazy(() => import(/* webpackChunkName: "FunksjonList" */  '../../features/funksjon/FunksjonList'));
const Organiseringtype = React.lazy(() => import(/* webpackChunkName: "OrganiseringTypeList" */  '../../features/organiseringstype/OrganiseringsTypeList'));
const Kurver = React.lazy(() => import(/* webpackChunkName: "KurverList" */  '../../features/kurver/kurvList'));
const Lagringsformater = React.lazy(() => import(/* webpackChunkName: "KurverList" */  '../../features/lagringsformater/LagringsformaterListe'));
const PresedensTagType = React.lazy(() => import(/* webpackChunkName: "PresedensTagType" */  '../../features/presedensTagType/PresedensmerketyperListe'));
const PresedensTag = React.lazy(() => import(/* webpackChunkName: "PresedensTagType" */  '../../features/presedensTag/PresedensmerkeListe'));
const Soner = React.lazy(() => import(/* webpackChunkName: "SoneListe" */  '../../features/soner/SoneListe'));
const FormatType = React.lazy(() => import(/* webpackChunkName: "FormatType" */  '../../features/formatType/FormatTypeListe'));
const Flytstatus = React.lazy(() => import(/* webpackChunkName: "PresedensTagListe" */  '../../features/flytstatus/FlytstatusListe'));
const Miljo = React.lazy(() => import(/* webpackChunkName: "MiljoList" */  '../../features/miljo/MiljoList'));
const Rollemaster = React.lazy(() => import(/* webpackChunkName: "RollemasterList" */  '../../features/rolle/RollemasterList'));
const Systemparameter = React.lazy(() => import(/* webpackChunkName: "SystemparameterList" */  '../../features/systemparameter/SystemparameterList'));
const DefaultProvider = React.lazy(() => import(/* webpackChunkName: "DefaultProviderList" */  '../../features/defaultProvider/DefaultProviderList'));
const IndekseringLogg = React.lazy(() => import(/* webpackChunkName: "IndekseringLoggList" */  '../../features/indekseringLogg/IndekseringLoggList'));
const Tilleggsdata = React.lazy(() => import(/* webpackChunkName: "TilleggsdataList" */  '../../features/tilleggsdata/TilleggsdataList'));
const Epostvarsler = React.lazy(() => import(/* webpackChunkName: "EpostVarslerListView" */  '../../features/varsling/EpostVarslerListView'));

const Referansekode = React.lazy(() => import(/* webpackChunkName: "ReferansekodeListe" */  '../../features/referanseKode/ReferansekodeList'));
const NodeMeny = React.lazy(() => import(/* webpackChunkName: "NodeMenyList" */  '../../features/nodeMeny/NodeMenyList'));
//#endregion System

const AnsvarstypeListe = React.lazy(() => import(/* webpackChunkName: "AnsvarstypeListe" */  '../../features/ansvarstype/ansvarstypeListe'));
const DispensasjonListe = React.lazy(() => import(/* webpackChunkName: "DispensasjonListe" */  '../../features/dispensasjon/dispensasjonListe'));
const FagomraadeListe = React.lazy(() => import(/* webpackChunkName: "FagomraadeListe" */  '../../features/fagomraade/fagomraadeListe'));
const Gebyrtyper = React.lazy(() => import(/* webpackChunkName: "GebyrtypeListe" */  '../../features/gebyrtyper/GebyrtypeListe'));
const SaksbehandlingStatusList = React.lazy(() => import(/* webpackChunkName: "SaksbehandlingStatusListe" */  '../../features/saksbehandlingStatus/SaksbehandlingStatusList'));
const TeksttabellList = React.lazy(() => import(/* webpackChunkName: "TeksttabellListe" */  '../../features/teksttabell/TeksttabellList'));
const SaksbehandlingstypeListe = React.lazy(() => import(/* webpackChunkName: "SaksbehandlingstypeListe" */  '../../features/saksbehandlingstype/SaksbehandlingstypeListe'));


const OverforingAdministrativEnhet = React.lazy(() => import(/* webpackChunkName: "OverforingAdministrativEnhet" */  '../../features/overforing/OverforingAdministrativEnhet'));
const PostnummerOppdateringView = React.lazy(() => import(/* webpackChunkName: "PostnummerOppdateringView" */  '../../features/postnummer/PostnummerOppdateringView'));
const Arsskifte = React.lazy(() => import(/* webpackChunkName: "ArsskifteDetailForm" */  '../../features/arsskifte/ArsskifteForm'));
const HelsesjekkDatabaseView = React.lazy(() => import(/* webpackChunkName: "HelsesjekkDatabaseView" */  '../../features/dbsjekk/HelsesjekkDatabaseView'));
const HomePageView = React.lazy(() => import(/* webpackChunkName: "HomePage" */  '../common/components/HomePage/HomePage'));
const RegeleditorMainForm = React.lazy(() => import(/* webpackChunkName: "RegeleditorMainForm" */  '../../features/regeleditor/RegeleditorMainForm'));


export type AppLink = {
  id: string;
  label: string;
  path: string;
  component?: JSX.Element;
  enabled: boolean;
  accessType?: TilgangstypeEnum;
};

export type AppLinkCategory = {
  id: string;
  label: string;
  icon?: JSX.Element;
  enabled: boolean;
  accessType?: TilgangstypeEnum;
  entries: AppLink[];
};
export const LAST_PAGE_URL = 'admin+_last_page_url';

export const homePage: AppLink = Object.freeze({
  id: 'HJEM',
  path: '/hjem',
  label: '',
  component: <HomePageView />,
  enabled: false,
});

function hasAccess(credentials: Credentials, link: AppLinkCategory | AppLink): boolean {
  if (link.id === 'search')
    return true;
  return Array.isArray(credentials?.availableNodes) && !!(link.id && credentials.availableNodes.find(f => f.nodeId === link.id));
}

function accessType(credentials: Credentials, link: AppLinkCategory | AppLink): TilgangstypeEnum {

  if (link.id && Array.isArray(credentials?.availableNodes) && credentials.availableNodes.find(f => f.nodeId === link.id)) {
    const accessType = link.accessType;
    return accessType as TilgangstypeEnum;
  }
  return 0;
}

export function useRoutes(): AppLinkCategory[] {
  const { t } = useTranslation();
  const auth = useAuth();
  const theme = useTheme();

  const result: AppLinkCategory[] = [
    {
      id: 'M_ADR',
      label: t('Adresse'),
      icon: <ContactsIcon />,
      enabled: false,
      entries: [
        {
          id: 'GFY',
          path: '/fylker',
          label: t('Fylker'),
          component: <FylkerListe />,
          enabled: false
        },
        {
          id: 'GGR',
          path: '/grupper',
          label: t('Grupper'),
          component: <Grupper />,
          enabled: false,
        },
        {
          id: 'GID',
          path: '/identiteter',
          label: t('Identiteter'),
          component: <IdentitetListe />,
          enabled: false,
        },
        {
          id: 'GKA',
          path: '/identitetskategorier',
          label: t('Identitetskategorier'),
          component: <Identitetskategorier />,
          enabled: false,
        },
        {
          id: 'GKO',
          path: '/kommuner',
          label: t('Kommuner'),
          component: <KommuneList />,
          enabled: false,
        },
        {
          id: 'GLA',
          path: '/land',
          label: t('Land'),
          component: <LandListe />,
          enabled: false,
        },
        {
          id: 'GPO',
          path: '/postnummer',
          label: t('Postnummer'),
          component: <PostnummerListe />,
          enabled: false,
        },
        {
          id: 'POSTNROPPD',
          path: '/postnoppd',
          label: t('Postnummer oppdatering'),
          component: <PostnummerOppdateringView />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_ARK',
      label: t('Arkiv'),
      icon: <AcosIcon color={theme.palette.action.active}>acos-arkiv</AcosIcon>,
      enabled: false,
      entries: [
        {
          id: 'NAD',
          path: '/arkivdeler',
          label: t('Arkivdeler'),
          component: <ArkivdelList />,
          enabled: false,
        },
        {
          id: 'NAR',
          path: '/arkiver',
          label: t('Arkiver'),
          component: <ArkivListe />,
          enabled: false,
        },
        {
          id: 'NAP',
          path: '/arkivperioder',
          label: t('Arkivperioder'),
          component: <ArkivPeriodeListe />,
          enabled: false,
        },
        {
          id: 'NAS',
          path: '/arkivstatuser',
          label: t('Arkivstatuser'),
          component: <ArkivStatusListe />,
          enabled: false,
        },
        {
          id: 'TAK',
          path: '/avskjermingskoder',
          label: t('Avskjermingskoder'),
          component: <AvskjermingKodeListe />,
          enabled: false,
        },
        {
          id: 'NBK',
          path: '/bortsettingskoder',
          label: t('Bortsettingskoder'),
          component: <BortsettingsKoderListe />,
          enabled: false,
        },
        {
          id: 'NJE',
          path: '/journalenheter',
          label: t('Journalenheter'),
          component: <Journalenheter />,
          enabled: false,
        },
        {
          id: 'NKK',
          path: '/kassasjonskoder',
          label: t('Kassasjonskoder'),
          component: <KassasjonsKodeListe />,
          enabled: false,
        },
        {
          id: 'NKB',
          path: '/kassasjonsbevaringsvedtak',
          label: t('Kassasjons- og bevaringsvedtak'),
          component: <KassasjonOgBeingsVedtakList />,
          enabled: false,
        },
        {
          id: 'LOV',
          path: '/lovverk',
          label: t('Lovverk'),
          component: <LovverkListe />,
          enabled: false,
        },
        {
          id: 'NOT',
          path: '/ordningsprinsipptyper',
          label: t('Ordningsprinsipptyper'),
          component: <OrdningsPrinsippTypeListe />,
          enabled: false,
        },
        {
          id: 'NOP',
          path: '/ordningsprinsipper',
          label: t('Ordningsprinsipper'),
          component: <OrdningsPrinsippListe />,
          enabled: false,
        },
        {
          id: 'NOV',
          path: '/ordningsverdier',
          label: t('Ordningsverdier'),
          component: <OrdningsVerdierList />,
          enabled: false,
        },
        {
          id: 'SPG',
          path: '/paragrafer',
          label: t('Paragrafer'),
          component: <ParagraferListe />,
          enabled: false,
        },
        {
          id: 'SGR',
          path: '/tilgangskoder',
          label: t('Tilgangskoder'),
          component: <TilgangsKodeListe />,
          enabled: false,
        },
        {
          id: 'ÅRSSKIFTE',
          path: '/årsskifte',
          label: t('Årsskifte'),
          component: <Arsskifte />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_DIV',
      label: t('Diverse'),
      icon: <AcosIcon color={theme.palette.action.active}>candy-outline</AcosIcon>,
      enabled: false,
      entries: [
        {
          id: 'ELI',
          path: '/eksternlink',
          label: t('Eksterne linker'),
          component: <EksterneLinker />,
          enabled: false,
        },
        {
          id: 'KKT',
          path: '/klokketyper',
          label: t('Klokketyper'),
          component: <Klokketyper />,
          enabled: false,
        },
        {
          id: 'TAG',
          path: '/tagger',
          label: t('Tag\'er'),
          component: <Tager />,
          enabled: false,
        },
      ],
    },

    {
      id: 'M_DOK',
      label: t('Dokument'),
      icon: <Description />,
      enabled: false,
      entries: [
        {
          id: 'NDK',
          path: '/dokumentkategori',
          label: t('Dokumentkategori'),
          component: <Dokumentkategories />,
          enabled: false,
        },
        {
          id: 'NDS',
          path: '/dokumentstatus',
          label: t('Dokumentstatuser'),
          component: <Dokumentstatus />,
          enabled: false,
        },
        {
          id: 'NDT',
          path: '/dokumenttilknytning',
          label: t('Dokumenttilknytning'),
          component: <DokumentTilknytning />,
          enabled: false,
        },
        {
          id: 'NVF',
          path: '/formatvarianter',
          label: t('Formater og varianter'),
          component: <FormatVariant />,
          enabled: false,
        },
        {
          id: 'NLF',
          path: '/lagringsformater',
          label: t('Lagringsformater'),
          component: <Lagringsformater />,
          enabled: false,
        },
        {
          id: 'GTM',
          path: '/tekstmaler',
          label: t('Tekstmaler'),
          component: <Tekstmaler />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_EI',
      label: t('Eiendom'),
      icon: <AcosIcon color={theme.palette.action.active}>acos-eiendom</AcosIcon>,
      enabled: false,
      entries: [
        {
          id: 'BAT',
          path: '/ansvarstyper',
          label: t('Ansvarstyper'),
          component: <AnsvarstypeListe />,
          enabled: false,
        },
        {
          id: 'GDT',
          path: '/dispensasjoner',
          label: t('Dispensasjoner'),
          component: <DispensasjonListe />,
          enabled: false,
        },
        {
          id: 'BFA',
          path: '/fagomraader',
          label: t('Fagområder'),
          component: <FagomraadeListe />,
          enabled: false,
        },
        {
          id: 'BGT',
          path: '/gebyrtyper',
          label: t('Gebyrtyper'),
          component: <Gebyrtyper />,
          enabled: false,
        },
        {
          id: 'SBHS',
          path: '/saksbehandlingstatuser',
          label: t('Saksbehandlingsstatuser'),
          component: <SaksbehandlingStatusList />,
          enabled: false,
        },
        {
          id: 'BST',
          path: '/saksbehandlingstyper',
          label: t('Saksbehandlingstyper'),
          component: <SaksbehandlingstypeListe />,
          enabled: false,
        },
        {
          id: 'BTT',
          path: '/teksttabeller',
          label: t('Teksttabell'),
          component: <TeksttabellList />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_JP',
      label: t('Journalpost'),
      icon: <AcosIcon color={theme.palette.action.active}>folder-text-outline</AcosIcon>,
      enabled: false,
      entries: [
        {
          id: 'SAM',
          path: '/avskrivingsmåter',
          label: t('Avskrivningsmåter'),
          component: <Avskrivningsmate />,
          enabled: false,
        },
        {
          id: 'SBT',
          path: '/behandlingstyper',
          label: t('Behandlingstyper'),
          component: <Behandlingstype />,
          enabled: false,
        },
        {
          id: 'SDT',
          path: '/dokumenttyper',
          label: t('Dokumenttyper'),
          component: <Dokumenttyper />,
          enabled: false,
        },
        {
          id: 'NFM',
          path: '/forsendelseskanaler',
          label: t('Forsendelseskanaler'),
          component: <Forsendelsesmate />,
          enabled: false,
        },
        {
          id: 'SAL',
          path: '/journalkategorier',
          label: t('Journalkategorier'),
          component: <Journalkategorier />,
          enabled: false,
        },
        {
          id: 'NJS',
          path: '/journalstatuser',
          label: t('Journalstatuser'),
          component: <Journalstatuser />,
          enabled: false,
        },
        {
          id: 'MALJOURNALPOST',
          path: '/maljournalposter',
          label: t('Mal for dokumenttype'),
          component: <MalJournalposter />,
          enabled: false,
        },
        {
          id: 'OPPGAVETYPER',
          path: '/oppgavertyper',
          label: t('Oppgaver'),
          component: <OppgaverList />,
          enabled: false,
        },
        {
          id: 'GPI',
          path: '/prioriteter',
          label: t('Prioriteter'),
          component: <Prioreteter />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_ORG',
      label: t('Organisasjon'),
      icon: <AcosIcon color={theme.palette.action.active}>sitemap</AcosIcon>,
      enabled: false,
      entries: [
        {
          id: 'SOA',
          path: '/admenheter',
          label: t('Administrative enheter'),
          component: <AdmEnheter />,
          enabled: false,
        },
        {
          id: 'SBR',
          path: '/brukere',
          label: t('Brukere'),
          component: <Brukere />,
          enabled: false,
        },
        {
          id: 'SET',
          path: '/enhetstyper',
          label: t('Enhetstyper'),
          component: <Enhetstyper />,
          enabled: false,
        },
        {
          id: 'ORG',
          path: '/organiseringer',
          label: t('Organiseringer'),
          component: <Organisering />,
          enabled: false,
        },
        {
          id: 'ORT',
          path: '/organiseringstyper',
          label: t('Organiseringstyper'),
          component: <Organiseringtype />,
          enabled: false,
        },
        {
          id: 'OVERFADMENH',
          path: '/overfør',
          label: t('Overfør administrativ enhet'),
          component: <OverforingAdministrativEnhet />,
          enabled: false,
        },
        {
          id: 'NTG',
          path: '/tilgangsgrupper',
          label: t('Tilgangsgrupper'),
          component: <TilgangsgruppeListe />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_SAK',
      label: t('Sak'),
      icon: <FolderIcon />,
      enabled: false,
      entries: [
        {
          id: 'SAG',
          path: '/avgjørelseskoder',
          label: t('Avgjørelseskoder'),
          component: <AvgjorelsesKode />,
          enabled: false,
        },
        {
          id: 'FORDELINGSSAKER',
          path: '/fordelingssaker',
          label: t('Fordelingssaker'),
          component: <ArkivSakList />,
          enabled: false,
        },
        {
          id: 'MALSAKER',
          path: '/malsaker',
          label: t('Mal for arkivsak'),
          component: <MalsakList />,
          enabled: false,
        },
        {
          id: 'SPF',
          path: '/partsforhold',
          label: t('Partsforhold'),
          component: <PartsforholdList />,
          enabled: false,
        },
        {
          id: 'NSS',
          path: '/sakstatus',
          label: t('Sakstatus'),
          component: <Sakstatus />,
          enabled: false,
        },
        {
          id: 'SKA',
          path: '/sakstyper',
          label: t('Sakstyper'),
          component: <SakstypeUniqueList />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_SYS',
      label: t('System'),
      icon: <AcosIcon color={theme.palette.action.active}>toolbox</AcosIcon>,
      enabled: false,
      entries: [
        {
          id: 'DBA',
          path: '/databaser',
          label: t('Databaser'),
          component: <Databaser />,
          enabled: false,
        },

        {
          id: 'DBSJEKK',
          path: '/dbsjekk',
          label: t('Databasesjekk'),
          component: <HelsesjekkDatabaseView />,
          enabled: false,
        },
        {
          id: 'GLO',
          path: '/endringslogg',
          label: t('Endringslogg'),
          component: <Logg />,
          enabled: false,
        },
        {
          id: 'FEB',
          path: '/felles-postbokser',
          label: t('Felles e-postbokser'),
          component: <FellesEpostbokser />,
          enabled: false,
        },
        {
          id: 'GFU',
          path: '/funksjoner',
          label: t('Funksjoner'),
          component: <Funksjon />,
          enabled: false,
        },
        {
          id: 'NIT',
          path: '/informasjonstyper',
          label: t('Informasjonstyper'),
          component: <Informasjonstyper />,
          enabled: false,
        },
        {
          id: 'GMN',
          path: '/kurver',
          label: t('Kurver'),
          component: <Kurver />,
          enabled: false,
        },
        {
          id: 'GMI',
          path: '/miljøer',
          label: t('Miljøer'),
          component: <Miljo />,
          enabled: false,
        },
        {
          id: 'PRT',
          path: '/presedenstag',
          label: t('Presedensmerke'),
          component: <PresedensTag />,
          enabled: false,
        },
        {
          id: 'PTT',
          path: '/presedenstagtype',
          label: t('Presedensmerketype'),
          component: <PresedensTagType />,
          enabled: false,
        },
        {
          id: 'REGELEDITOR',
          path: '/regeleditor',
          label: t('Regelsett'),
          component: <RegeleditorMainForm />,
          enabled: false,
        },
        {
          id: 'GRM',
          path: '/roller',
          label: t('Roller'),
          component: <Rollemaster />,
          enabled: false,
        },
        { id: 'GSO', path: '/soner', label: t('Soner'), component: <Soner />, enabled: false },
        {
          id: 'GSP',
          path: '/spraak',
          label: t('Språk'),
          component: <SpraakListe />,
          enabled: false,
        },
        {
          id: 'GSY',
          path: '/systemparameter',
          label: t('Systemparameter'),
          component: <Systemparameter />,
          enabled: false,
        },
        {
          id: 'SOK',
          path: '/soek',
          label: t('Søk+'),
          component: <SearchClient />,
          enabled: true,
        },
        {
          id: 'INDL',
          path: '/indekseringlogg',
          label: t('Søkeindeksering'),
          component: <IndekseringLogg />,
          enabled: true,
        },
        {
          id: 'SPR',
          path: '/søkekilder',
          label: t('Søkekilder'),
          component: <DefaultProvider />,
          enabled: false,
        },
        {
          id: 'GTK',
          path: '/tekstkataloger',
          label: t('Tekstkataloger'),
          component: <Tekstkataloger />,
          enabled: false,
        },
        {
          id: 'GTF',
          path: '/tilgangsfunksjoner',
          label: t('Tilgangsfunksjoner'),
          component: <Tilgangsfunksjoner />,
          enabled: false,
        },
        {
          id: 'GNM',
          path: '/tilgangsstyring',
          label: t('Tilgangsstyring'),
          component: <NodeMeny />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_TGD',
      label: t('Tilleggsdata'),
      icon: <AcosIcon color={theme.palette.action.active}>expand-all</AcosIcon>,
      enabled: false,
      entries: [
        {
          id: 'VSD',
          path: '/tilleggsdata',
          label: t('Tilleggsdata'),
          component: <Tilleggsdata />,
          enabled: false,
        },
        {
          id: 'VFS',
          path: '/tilleggsdata-flytstatuser',
          label: t('Flytstatuser'),
          component: <Flytstatus />,
          enabled: false,
        },
        {
          id: 'DFT',
          path: '/tilleggsdata-formater',
          label: t('Formater'),
          component: <FormatType />,
          enabled: false,
        },
        {
          id: 'VRF',
          path: '/tilleggsdata-referansekoder',
          label: t('Referansekoder'),
          component: <Referansekode />,
          enabled: false,
        },
      ],
    },
    {
      id: 'M_GVA',
      label: t('Varsling'),
      icon: <NotificationsIcon>expand-all</NotificationsIcon>,
      enabled: false,
      entries: [
        {
          id: 'GVA',
          path: '/epostvarsler',
          label: t('E-postvarsler'),
          component: <Epostvarsler />,
          enabled: false,
        }
      ],
    }
  ];

  if (!auth)
    return result;
  for (const category of result) {
    category.enabled = hasAccess(auth, category);
    category.accessType = accessType(auth, category);

    for (const entry of category.entries) {
      entry.enabled = hasAccess(auth, entry);
      entry.accessType = accessType(auth, entry);
    }
  }

  return result;
}
const DeactivatedSearchPages = ['ÅRSSKIFTE', 'POSTNROPPD', 'DBSJEKK', 'OVERFADMENH'];
const DeactivatedExtendedSearchPages = ['FORDELINGSSAKER', 'search'];
export function useDeactivateExtendedSearch(url: string): boolean {
  const pages = DeactivatedExtendedSearchPages;
  const routes = useRoutes();
  let deactivate = false;
  routes.forEach(i => {
    i.entries.forEach(j => {
      if (pages.includes(j.id) && j.path === url)
        deactivate = true;
    });
  });
  return deactivate;
}

export function useDeactivateSearch(url: string): boolean {
  const pages = DeactivatedSearchPages;
  const routes = useRoutes();
  let deactivate = false;
  routes.forEach(i => {
    i.entries.forEach(j => {
      if (pages.includes(j.id) && j.path === url)
        deactivate = true;
    });
  });
  return deactivate;
}

export function useBrowserRouter() {
  const pathname = useBase();
  const routes = useRoutes();
  let lastUrl = localStorage.getItem(LAST_PAGE_URL);

  const initRoutes: RouteObject[] = [
    {
      element: <AppLayout />,
      children: [
        {
          path: '',
          element: <Navigate to={lastUrl ?? homePage.path} />,
        },
        {
          path: homePage.path,
          element: homePage.component ? homePage.component : <NotImplemented />,
        },
        ...routes.flatMap((category) => category.entries.map(route => ({
          key: route.path,
          path: route.path + '/*',
          element: route.component ? route.component : <NotImplemented />,
        }))),
        {
          path: '*',
          element: <NoMatch />,
        },
      ],
    },
  ];
  return createBrowserRouter(initRoutes, {
    basename: pathname.replace(/\/$/i, ''),
  });
}


